/*
 * Copyright (c) 2026 Joseph Duggan. All rights reserved.
 */

import { appPalette } from '@sidekick/design-tokens';
import { useState, type FormEvent } from 'react';
import ReactMarkdown from 'react-markdown';
import { localTimezone, monoLabel, inputStyle } from '../../lib/ui.js';
import { trpc } from '../../lib/trpc.js';

const p = appPalette;

const MAX_TOMORROW = 3;

interface TomorrowRow {
  description: string;
  linkedActionId: string | null;
}

export function Shutdown({ briefDate }: { briefDate: string }) {
  const run = trpc.shutdown.run.useMutation();
  const actionsQuery = trpc.actions.list.useQuery();
  const [accomplishments, setAccomplishments] = useState('');
  const [knownTomorrow, setKnownTomorrow] = useState('');
  const [tomorrowCommitments, setTomorrowCommitments] = useState<TomorrowRow[]>([
    { description: '', linkedActionId: null },
  ]);

  const nextActions = (actionsQuery.data ?? []).filter((a) => a.status === 'next');

  const updateRow = (index: number, next: TomorrowRow) => {
    setTomorrowCommitments((prev) => prev.map((r, i) => (i === index ? next : r)));
  };
  const removeRow = (index: number) => {
    setTomorrowCommitments((prev) => prev.filter((_, i) => i !== index));
  };
  const addRow = () => {
    setTomorrowCommitments((prev) =>
      prev.length >= MAX_TOMORROW ? prev : [...prev, { description: '', linkedActionId: null }],
    );
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (accomplishments.trim() === '') return;
    const tomorrow = tomorrowCommitments
      .map((r) => ({ description: r.description.trim(), linkedActionId: r.linkedActionId }))
      .filter((r) => r.description !== '')
      .slice(0, MAX_TOMORROW);
    run.mutate({
      briefDate,
      timezone: localTimezone(),
      accomplishments: accomplishments.trim(),
      knownTomorrow: knownTomorrow.trim() === '' ? null : knownTomorrow.trim(),
      ...(tomorrow.length > 0 ? { tomorrowCommitments: tomorrow } : {}),
    });
  };

  const result = run.data;

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ ...monoLabel }}>Close the day</div>

      {result ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div className="serif" style={{ fontSize: 16, lineHeight: 1.55, color: p.fg }}>
            <ReactMarkdown>{result.summaryMarkdown}</ReactMarkdown>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ ...monoLabel }}>Tomorrow</span>
            <p
              style={{
                fontFamily: 'var(--font-serif)',
                fontFeatureSettings: 'var(--serif-feat)',
                fontSize: 15,
                color: p.fg,
                margin: 0,
              }}
            >
              {result.tomorrowPreview}
            </p>
          </div>
          <p
            style={{
              fontFamily: 'var(--font-serif)',
              fontFeatureSettings: 'var(--serif-feat)',
              fontStyle: 'italic',
              fontSize: 15,
              color: p.muted,
              margin: 0,
            }}
          >
            {result.closingObservation}
          </p>
        </div>
      ) : (
        <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <textarea
            value={accomplishments}
            onChange={(e) => setAccomplishments(e.target.value)}
            placeholder="What got done today?"
            aria-label="What got done today?"
            rows={3}
            style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }}
          />
          <textarea
            value={knownTomorrow}
            onChange={(e) => setKnownTomorrow(e.target.value)}
            placeholder="Anything you already know about tomorrow? (optional)"
            aria-label="Anything you already know about tomorrow?"
            rows={2}
            style={{ ...inputStyle, resize: 'vertical', minHeight: 56 }}
          />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
            <span style={{ ...monoLabel }}>Tomorrow&apos;s commitments (optional)</span>
            {tomorrowCommitments.map((row, i) => (
              <TomorrowRowInput
                key={i}
                index={i}
                row={row}
                nextActions={nextActions}
                onChange={(next) => updateRow(i, next)}
                onRemove={() => removeRow(i)}
              />
            ))}
            <button
              type="button"
              onClick={addRow}
              disabled={tomorrowCommitments.length >= MAX_TOMORROW}
              aria-label="Add tomorrow commitment"
              style={{
                alignSelf: 'flex-start',
                background: 'transparent',
                border: 'none',
                color: tomorrowCommitments.length >= MAX_TOMORROW ? p.line : p.muted,
                fontSize: 12,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                cursor: tomorrowCommitments.length >= MAX_TOMORROW ? 'not-allowed' : 'pointer',
                padding: '0 4px',
              }}
            >
              + Add
            </button>
          </div>

          <button
            type="submit"
            disabled={run.isPending || accomplishments.trim() === ''}
            style={{
              alignSelf: 'flex-start',
              padding: '10px 18px',
              background: p.fg,
              color: p.bg,
              border: 'none',
              fontSize: 13,
              cursor: run.isPending || accomplishments.trim() === '' ? 'not-allowed' : 'pointer',
              opacity: run.isPending || accomplishments.trim() === '' ? 0.6 : 1,
            }}
          >
            {run.isPending ? 'Shutting down…' : 'Shut down →'}
          </button>
        </form>
      )}
      {run.error ? (
        <span style={{ ...monoLabel, color: p.accent }}>{run.error.message}</span>
      ) : null}
    </section>
  );
}

function TomorrowRowInput({
  index,
  row,
  nextActions,
  onChange,
  onRemove,
}: {
  index: number;
  row: TomorrowRow;
  nextActions: ReadonlyArray<{ id: string; description: string }>;
  onChange: (next: TomorrowRow) => void;
  onRemove: () => void;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto auto',
        gap: 8,
        alignItems: 'center',
      }}
    >
      <input
        type="text"
        value={row.description}
        onChange={(e) => onChange({ description: e.target.value, linkedActionId: null })}
        placeholder={`Tomorrow commitment ${index + 1}`}
        aria-label={`Tomorrow commitment ${index + 1}`}
        style={{ ...inputStyle, width: '100%' }}
      />
      <select
        value={row.linkedActionId ?? ''}
        onChange={(e) => {
          const id = e.target.value;
          if (id === '') return;
          const action = nextActions.find((a) => a.id === id);
          if (!action) return;
          onChange({ description: action.description, linkedActionId: action.id });
        }}
        aria-label={`Tomorrow commitment ${index + 1} from a next action`}
        style={{
          ...inputStyle,
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          letterSpacing: '0.06em',
          padding: '10px 8px',
          maxWidth: 170,
        }}
      >
        <option value="">from a next action…</option>
        {nextActions.map((a) => (
          <option key={a.id} value={a.id}>
            {a.description}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove tomorrow commitment ${index + 1}`}
        title="Remove"
        style={{
          background: 'transparent',
          border: 'none',
          color: p.muted,
          cursor: 'pointer',
          fontSize: 16,
          padding: '0 4px',
        }}
      >
        ×
      </button>
    </div>
  );
}
